import { Card, DescriptionList } from '@circlefin/components'
import { CopyToClipboard } from '@circlefin/components/lib/CopyToClipboard'
import { getBlockchain } from '@services/static-data/blockchains'
import { useBlockchainRecipientAddressWithMetadataQuery } from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import { WalletNickname } from '../../components'

import { RemoveAddress } from './RemoveAddress/RemoveAddress'
import { Status } from './Status/Status'

const { Label, Description } = DescriptionList

export interface WalletInformationProps {
  /*
   * Recipient Address ID.
   */
  addressId: string
}

export const WalletInformation: React.FC<WalletInformationProps> = ({
  addressId,
}) => {
  const { t } = useTranslation('address-book/containers')
  const { data } = useBlockchainRecipientAddressWithMetadataQuery({
    variables: {
      id: addressId,
    },
  })

  if (!data?.blockchainRecipientAddressWithMetadata) {
    return null
  }

  const {
    chain,
    description,
    email,
    address,
    delayedWithdrawalInfo,
    addressTag,
    status,
  } = data.blockchainRecipientAddressWithMetadata

  const blockchainName = getBlockchain(chain)?.name

  return (
    <Card>
      <Card.Content className="px-10 py-4 font-circular">
        <div data-testid="wallet-information-card">
          <div className="ml-4 mt-4 pb-2 text-xl leading-8 font-circular-book">
            {t`wallet-information.title`}
          </div>
          <div className="flex flex-col">
            <DescriptionList
              className="ml-4 pt-5"
              data-testid="wallet-description-list"
              density="loose"
            >
              <Status
                delayedWithdrawalInfo={delayedWithdrawalInfo}
                status={status}
              />

              <Label className="flex w-72 items-center">{t`wallet-information.nickname`}</Label>
              <Description>
                <WalletNickname
                  nickname={description ?? ''}
                  recipientAddressWithMetadata={
                    data.blockchainRecipientAddressWithMetadata
                  }
                />
              </Description>

              {email && (
                <>
                  <Label className="w-72">{t`wallet-information.email`}</Label>
                  <Description>{email}</Description>
                </>
              )}

              <Label className="w-72">{t`wallet-information.blockchain`}</Label>
              <Description>{blockchainName}</Description>

              <Label className="w-72">
                {t('wallet-information.walletAddress', {
                  chain: blockchainName,
                })}
              </Label>
              <Description className="flex items-center">
                <CopyToClipboard value={address} variant="inline" />
              </Description>
              <Label>{t('wallet-information.memo')}</Label>
              <Description>{addressTag ?? '--'}</Description>
            </DescriptionList>

            <RemoveAddress
              addressId={addressId}
              className="mb-4 ml-4 border-t border-neutral-subtle py-4"
              nickname={description ?? ''}
            />
          </div>
        </div>
      </Card.Content>
    </Card>
  )
}
